import axios, { isAxiosError } from 'axios'
import { jwtDecode } from 'jwt-decode'

const TOKEN_STORAGE_KEY = 'MEGA_API_TOKEN'

/**
 * The sign-in worked but this account has no active Insights profile — the
 * backend answered 403. Not retryable; the fix is an admin granting access.
 */
export class NoInsightsAccessError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'NoInsightsAccessError'
  }
}

/**
 * Exchange Auth0 token for our application token
 */
export const exchangeToken = async (auth0Token: string): Promise<{ token: string }> => {
  try {
    if (!auth0Token || typeof auth0Token !== 'string' || auth0Token.trim() === '') {
      throw new Error('Invalid Auth0 token format: token is empty or not a string')
    }

    const tokenParts = auth0Token.split('.')
    if (tokenParts.length !== 3) {
      throw new Error('Invalid Auth0 token format: not a valid JWT')
    }

    const response = await axios.post<{ token: string }>(
      `${import.meta.env.VITE_MEGALODON_SERVER_URL || 'http://localhost:3009'}/auth/token`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth0Token}`,
        },
        timeout: 15000,
      }
    )

    if (!response.data || !response.data.token) {
      throw new Error('Invalid token response from server: missing token data')
    }

    return response.data
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 500) {
          throw new Error(
            `Server error during token exchange: ${error.response.data?.message || 'Internal server error'}`
          )
        } else if (error.response.status === 401) {
          throw new Error('Invalid or expired Auth0 token')
        } else if (error.response.status === 403) {
          throw new NoInsightsAccessError(error.response.data?.message || 'This account does not have access yet')
        } else if (error.response.status === 429) {
          throw new Error('Rate limit exceeded: Too many requests')
        }
      } else if (error.request) {
        throw new Error('Network error: No response from server')
      }
    }
    if (error instanceof NoInsightsAccessError) {
      throw error
    }
    throw new Error(`Failed to exchange token: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Ask an admin for Insights access. Authenticated by the Auth0 login token —
 * a user in this position can never hold an app token.
 */
export const requestAccess = async (auth0Token: string): Promise<void> => {
  await axios.post(
    `${import.meta.env.VITE_MEGALODON_SERVER_URL || 'http://localhost:3009'}/auth/request-access`,
    {},
    {
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth0Token}` },
      timeout: 15000,
    }
  )
}

/**
 * Exchange Auth0 token with retry logic for server errors
 */
export async function exchangeTokenWithRetry(auth0Token: string, maxRetries: number = 3): Promise<{ token: string }> {
  let lastError: Error | undefined

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await exchangeToken(auth0Token)
    } catch (error) {
      lastError = error as Error

      const isServerError = error instanceof Error && error.message.includes('Server error during token exchange')
      const isNetworkError = error instanceof Error && error.message.includes('Network error')

      if (!isServerError && !isNetworkError) {
        throw error
      }

      if (attempt === maxRetries) {
        break
      }

      const delayMs = Math.min(1000 * Math.pow(2, attempt - 1), 5000)
      await new Promise((resolve) => setTimeout(resolve, delayMs))
    }
  }

  throw lastError || new Error('Token exchange failed with unknown error')
}

/**
 * Save token to localStorage
 */
export const saveToken = (token: string): void => {
  localStorage.setItem(TOKEN_STORAGE_KEY, token)
}

/**
 * Get stored token if it exists
 */
export const getStoredToken = (): string | null => {
  return localStorage.getItem(TOKEN_STORAGE_KEY)
}

/**
 * Validate if a token looks properly formatted
 */
export const isValidTokenFormat = (token: string | null): boolean => {
  if (!token) return false

  const tokenParts = token.split('.')

  if (tokenParts.length !== 3) {
    return false
  }

  for (let i = 0; i < tokenParts.length; i++) {
    if (!tokenParts[i] || tokenParts[i].trim() === '') {
      return false
    }
    if (!/^[A-Za-z0-9_-]+={0,2}$/.test(tokenParts[i])) {
      return false
    }
  }

  return true
}

/**
 * Check if a JWT token is expired (with 30s buffer)
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<{ exp?: number }>(token)
    if (!decoded.exp) return true
    // Expired if less than 30 seconds remaining
    return decoded.exp * 1000 < Date.now() + 30_000
  } catch {
    return true
  }
}

/**
 * Enhanced version of getStoredToken that validates format and expiry
 */
export const getStoredTokenWithValidation = (): string | null => {
  const token = getStoredToken()

  if (token && !isValidTokenFormat(token)) {
    clearToken()
    return null
  }

  if (token && isTokenExpired(token)) {
    clearToken()
    return null
  }

  return token
}

/**
 * Clear stored token
 */
export const clearToken = (): void => {
  localStorage.removeItem(TOKEN_STORAGE_KEY)
}

/**
 * Get the current stored token (alias for getStoredToken for permission utils)
 */
export const getToken = (): string | null => {
  return getStoredToken()
}
